import { InventoryTransactionType, ProductInventoryDoc, ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { Button, Card, DatePicker, Form, Input, InputNumber, Radio, Select, message } from 'antd';
import { Dayjs } from 'dayjs';
import { FC, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';

import { formatNumber } from 'src/utils/common';
import { formatDate, getDefaultDate } from 'src/utils/date-util';
import { callInventoryIssueTransaction } from 'src/utils/firebase/firebase-callable';
import { sortUomUnit } from 'src/utils/product-inventory-util';

import ProductInventorySelector from 'src/atomic-components/atoms/Seletor/ProductInventorySelector';

import classes from './InventoryIssueForm.module.scss';

interface InventoryIssueFormValues {
  date: Dayjs;
  inventoryId: string | undefined;
  inventoryUnit: ProductInventoryUnit;
  transactionType: InventoryTransactionType;
  requestValue: number;
  reason: string;
}

const defaultIssueDate = getDefaultDate();
const initialValues: InventoryIssueFormValues = {
  date: defaultIssueDate,
  inventoryId: undefined,
  inventoryUnit: ProductInventoryUnit.EA,
  transactionType: InventoryTransactionType.INBOUND,
  requestValue: 1,
  reason: '',
};

/**
 * 재고 이슈 폼 데이터 검증 함수
 * @param values 검증할 폼 데이터
 * @returns 검증 결과 (성공 시 true, 실패 시 false)
 */
const validateInventoryIssueForm = (values: InventoryIssueFormValues): { isValid: boolean; errorMessage?: string } => {
  const { date, inventoryId, inventoryUnit, requestValue, reason, transactionType } = values;

  // 날짜 검증
  if (!date) {
    return { isValid: false, errorMessage: '이슈 날짜를 선택해주세요.' };
  }

  // 미래 날짜 검증
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selectedDate = date.toDate();
  selectedDate.setHours(0, 0, 0, 0);
  if (selectedDate > today) {
    return { isValid: false, errorMessage: '미래 날짜는 선택할 수 없습니다.' };
  }

  // 재고 ID 검증
  if (!inventoryId) {
    return { isValid: false, errorMessage: '재고를 선택해주세요.' };
  }

  // 단위 검증
  if (!inventoryUnit) {
    return { isValid: false, errorMessage: '단위를 선택해주세요.' };
  }

  // 수량 검증
  if (!requestValue || requestValue <= 0) {
    return { isValid: false, errorMessage: '수량은 1 이상이어야 합니다.' };
  }

  // 이슈 내용 검증
  if (!reason || reason.trim() === '') {
    return { isValid: false, errorMessage: '이슈 내용을 입력해주세요.' };
  }

  // 이슈 내용 길이 검증
  if (reason.length > 100) {
    return { isValid: false, errorMessage: '이슈 내용은 100자 이내로 입력해주세요.' };
  }

  // 출고 무제한 검증
  if (transactionType === InventoryTransactionType.OUTBOUND && requestValue === Infinity) {
    return { isValid: false, errorMessage: '출고 무제한은 입력할 수 없습니다.' };
  }

  return { isValid: true };
};

const InventoryIssueForm: FC = () => {
  const { user } = useAuth();
  // 재고 이슈 생성 폼 상태 관리
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState<ProductInventoryDoc | undefined>(undefined);

  const unitOptions = sortUomUnit(Object.keys(inventory?.uom ?? {})).map((unit) => ({
    label: unit,
    value: unit,
  }));

  const handleInventoryChange = (productInventory: ProductInventoryDoc) => {
    setInventory(productInventory);
    form.setFieldsValue({ inventoryId: productInventory._id });
    if (productInventory.uom) {
      const unit = sortUomUnit(Object.keys(productInventory.uom))[0];
      form.setFieldsValue({ inventoryUnit: unit as ProductInventoryUnit });
    }
  };

  const handleSubmit = async (values: InventoryIssueFormValues) => {
    if (!user) {
      message.error('로그인 후 이용해주세요.');
      return;
    }

    // 폼 데이터 검증
    const validation = validateInventoryIssueForm(values);
    if (!validation.isValid) {
      message.error(validation.errorMessage);
      return;
    }

    setLoading(true);
    try {
      const { date, inventoryId, inventoryUnit, transactionType, requestValue, reason } = values;
      const transactionTypeText = transactionType === InventoryTransactionType.INBOUND ? '입고' : '출고';
      const issueDate = formatDate(date.toDate(), 'yyyy-MM-dd');

      if (!inventoryId) {
        return;
      }

      const res = await callInventoryIssueTransaction({
        body: {
          issueDate,
          inventoryId,
          inventoryUnit,
          transactionType,
          requestValue,
          reason,
        },
      });

      if (res.data.result === 'success') {
        message.success(`${transactionTypeText} 이슈 요청 전송 완료.`);
      } else {
        message.error(`${transactionTypeText} 이슈 요청 실패. error: ${res.data.reason}`);
      }

      // 현재 transactionType 값을 보존
      const currentTransactionType = form.getFieldValue('transactionType');
      form.resetFields();
      form.setFieldValue('transactionType', currentTransactionType);
    } catch (error) {
      console.error('재고 이슈 생성 오류:', error);
      message.error('재고 이슈 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title='재고 이슈 생성' style={{ marginBottom: 16 }}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        initialValues={initialValues}
        className={classes.inventoryIssueForm__twoRowLayout}>
        <div className={classes.inventoryIssueForm__topRow}>
          <Form.Item
            name='date'
            label='이슈 날짜'
            style={{ flex: 1 }}
            rules={[{ required: true, message: '이슈 날짜를 선택해주세요' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name='inventoryId'
            label='재고'
            style={{ flex: 2 }}
            rules={[{ required: true, message: '재고를 선택해주세요' }]}>
            <ProductInventorySelector handleProductInventoryChange={handleInventoryChange} />
          </Form.Item>

          <Form.Item
            name='inventoryUnit'
            label='단위'
            style={{ flex: 1 }}
            rules={[{ required: true, message: '단위을 선택해주세요' }]}>
            <Select disabled={!inventory} placeholder='단위 선택' options={unitOptions} />
          </Form.Item>

          <Form.Item
            label='현 재고량'
            style={{ width: 140 }}
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.inventoryUnit !== currentValues.inventoryUnit ||
              prevValues.inventoryId !== currentValues.inventoryId
            }>
            {(formInstance) => {
              if (!inventory) return <div>-</div>;

              const unit = formInstance.getFieldValue('inventoryUnit') as ProductInventoryUnit;
              const ratio = inventory?.uom?.[unit]?.ratio;

              return (
                <b>
                  {inventory.quantity === Infinity
                    ? '무제한'
                    : (() => {
                        if (ratio) {
                          const convertedQuantity = inventory.quantity / ratio;
                          return `${inventory.quantity} (${formatNumber(convertedQuantity, 2)} ${unit})`;
                        }
                        return `${inventory.quantity}`;
                      })()}
                </b>
              );
            }}
          </Form.Item>

          <Form.Item
            name='requestValue'
            label='수량'
            style={{ flex: 1 }}
            rules={[{ required: true, message: '수량을 입력해주세요' }]}>
            <InputNumber
              min={1}
              style={{ width: '100%' }}
              addonAfter={
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => {
                    if (prevValues.transactionType !== currentValues.transactionType) {
                      // 출고로 변경되고 현재 값이 무제한이면 0으로 변경
                      if (
                        currentValues.transactionType === InventoryTransactionType.OUTBOUND &&
                        form.getFieldValue('requestValue') === Infinity
                      ) {
                        form.setFieldsValue({ requestValue: 0 });
                      }
                      return true;
                    }
                    return false;
                  }}>
                  {({ getFieldValue }) => (
                    <Button
                      size='small'
                      type='text'
                      onClick={() => form.setFieldsValue({ requestValue: Infinity })}
                      disabled={getFieldValue('transactionType') === InventoryTransactionType.OUTBOUND}
                      style={{ padding: '0 8px' }}>
                      무제한
                    </Button>
                  )}
                </Form.Item>
              }
            />
          </Form.Item>
        </div>

        <div className={classes.inventoryIssueForm__bottomRow}>
          <Form.Item name='transactionType' label='입출고 유형'>
            <Radio.Group>
              <Radio.Button value={InventoryTransactionType.INBOUND}>입고</Radio.Button>
              <Radio.Button value={InventoryTransactionType.OUTBOUND}>출고</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name='reason'
            label='이슈 내용'
            style={{ flex: 1 }}
            rules={[{ required: true, message: '이슈 내용을 입력해주세요' }]}>
            <Input placeholder='이슈 내용을 입력하세요' />
          </Form.Item>

          <div className={classes.inventoryIssueForm__buttonWrapper}>
            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.transactionType !== currentValues.transactionType
              }>
              {({ getFieldValue }) => (
                <Button
                  type='primary'
                  danger={getFieldValue('transactionType') === InventoryTransactionType.OUTBOUND}
                  htmlType='submit'
                  loading={loading}>
                  {getFieldValue('transactionType') === InventoryTransactionType.INBOUND ? '입고 처리' : '출고 처리'}
                </Button>
              )}
            </Form.Item>
          </div>
        </div>
      </Form>
    </Card>
  );
};

export default InventoryIssueForm;
